import { slideDown, slideUp } from './animate';
import { smoothScrollToElement } from './jquery';

const SURVEY_FIELD_CLASS = 'en__field--survey';

/**
 * Add EN error message to the given field
 */
export const addErrorMessageToField = (
  field: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
  message: string,
) => {
  const existingErrorMessage = field.closest('.en__field')?.querySelector('.en__field__error');

  if (existingErrorMessage) {
    existingErrorMessage.textContent = message;
    return;
  }

  const errorMessage = document.createElement('div');
  errorMessage.classList.add('en__field__error');
  errorMessage.textContent = message;

  field.closest('.en__field')?.appendChild(errorMessage);
};

// 'Please choose an amount' error message by language code; falls back to English
const DONATION_AMOUNT_ERROR_TEXT: Record<string, string> = {
  de: 'Bitte wählen Sie einen Betrag',
  en: 'Please choose an amount',
  es: 'Por favor, elige una cantidad',
  fr: 'Veuillez choisir un montant',
  nb: 'Velg et beløp',
  nn: 'Velg et beløp',
  no: 'Velg et beløp',
  sv: 'Välj ett belopp',
};

export const customiseErrorMessages = () => {
  // Language code only, e.g. 'en' from 'en-GB'; falls back to English for unsupported languages
  const language = (window.pageJson?.locale || 'en').split(/[-_]/)[0].toLowerCase();
  const donationAmountErrorText =
    DONATION_AMOUNT_ERROR_TEXT[language] || DONATION_AMOUNT_ERROR_TEXT.en;

  window.onENValidateSetupCompleteArray = window.onENValidateSetupCompleteArray || [];
  window.onENValidateSetupCompleteArray.push(() => {
    window.changeFieldErrorMessage('fieldName', 'transaction.donationAmt', donationAmountErrorText);
  });
};

/**
 * Handle floating button on mobile to skip to form
 */
export const mobileFloatingSkipToFormButton = () => {
  const floatingSkipToFormButtonRow = document.querySelector(
    '.wp-floating-skip-to-form',
  ) as HTMLAnchorElement | null;

  if (!floatingSkipToFormButtonRow) return;

  const floatingSkipToFormButton = floatingSkipToFormButtonRow.querySelector(
    '.wp-floating-skip-to-form-button a',
  ) as HTMLButtonElement | null;

  if (!floatingSkipToFormButton) {
    console.error('Could not find floating skip to form button');

    return;
  }

  const formColumn = document.querySelector('.wp-form-column') as HTMLElement | null;
  const firstFormBlock = document.querySelector('.en__component--formblock') as HTMLElement | null;

  if (!formColumn && !firstFormBlock) {
    console.error(
      'Could not find form column or form block for floating mobile skip to form button',
    );

    return;
  }

  const scrollTarget = formColumn || (firstFormBlock as HTMLElement);
  scrollTarget.setAttribute('id', 'form');

  floatingSkipToFormButton.addEventListener('click', (event) => {
    event.preventDefault();
    smoothScrollToElement(scrollTarget);

    // Remove focus from the button so that it doesn't still have focused colours if it reappears
    floatingSkipToFormButton.blur();
  });

  // Hide button when the scroll target is in view (re-show it when scroll target is out of view)
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) slideUp(floatingSkipToFormButtonRow);
        else slideDown(floatingSkipToFormButtonRow);
      });
    },
    { threshold: 0.1 },
  );

  observer.observe(scrollTarget);
};

/**
 * Remove EN error message from the given field
 */
export const removeErrorMessageFromField = (
  field: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
) => {
  const existingErrorMessage = field.closest('.en__field')?.querySelector('.en__field__error');

  if (existingErrorMessage) existingErrorMessage.remove();
};

/**
 * Set certain elements in forms to be shown side by side.
 */
export const sideBySideElements = () => {
  const SIDE_BY_SIDE_CONTAINER_CLASS = 'wp-side-by-side-container';
  const SIDE_BY_SIDE_CLASS = 'wp-side-by-side';
  const SIDE_BY_SIDE_MAX_ELEMENTS = 2; // the maximum number of elements that can be set side by side - if more than this, they will stack as normal
  const SIDE_BY_SIDE_MAX_LABEL_LENGTH = 10; // the maximum number of characters in the label for an element to be considered for side by side - if label is longer than this, element will not be set side by side as it may not fit

  const FIRST_NAME_SELECTOR = '.en__field--firstName';
  const LAST_NAME_SELECTOR = '.en__field--lastName';

  // Stack first name and last name fields side by side if they are both present and next to each other
  const firstNameField = document.querySelector(FIRST_NAME_SELECTOR);
  const lastNameField = document.querySelector(LAST_NAME_SELECTOR);

  if (firstNameField && lastNameField) {
    const firstNameFieldNextSibling = firstNameField.nextElementSibling;

    if (firstNameFieldNextSibling === lastNameField) {
      firstNameField.classList.add(SIDE_BY_SIDE_CLASS);
      lastNameField.classList.add(SIDE_BY_SIDE_CLASS);
      firstNameField.parentElement?.classList.add(SIDE_BY_SIDE_CONTAINER_CLASS);
    }
  }

  // Look for checkboxes and radios to stack side by side

  const checkboxesFields = document.querySelectorAll('.en__field--checkbox');

  checkboxesFields.forEach((field) => {
    if (field.classList.contains(SURVEY_FIELD_CLASS)) return;

    const elements = field.querySelectorAll('.en__field__element--checkbox');

    if (elements.length === 1) {
      field.classList.add(SIDE_BY_SIDE_CLASS);
      field.parentElement?.classList.add(SIDE_BY_SIDE_CONTAINER_CLASS);
    }
  });

  const checkboxesRadiosElements = document.querySelectorAll(
    '.en__field__element--checkbox, .en__field__element--radio',
  );

  checkboxesRadiosElements.forEach((element) => {
    let sideBySide = true;

    const fieldItems = element.querySelectorAll('.en__field__item');
    if (fieldItems.length > SIDE_BY_SIDE_MAX_ELEMENTS) sideBySide = false;

    fieldItems.forEach((fieldItem) => {
      const label = fieldItem.querySelector('.en__field__label--item');

      if (label?.textContent?.length && label.textContent.length > SIDE_BY_SIDE_MAX_LABEL_LENGTH)
        sideBySide = false;
    });

    if (sideBySide) {
      element.classList.add(SIDE_BY_SIDE_CLASS);
      element.parentElement?.classList.add(SIDE_BY_SIDE_CONTAINER_CLASS);
    }
  });
};
